/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { motion } from 'framer-motion';
import {
    Briefcase, MessageSquareQuote, Star, Eye, Inbox, RefreshCcw,
} from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };

const STEPS = [
    {
        icon: Briefcase,
        title: 'Accompagnement',
        desc: "Vous suivez un vendeur dans Homly. Estimations, visites, comptes-rendus.",
    },
    {
        icon: MessageSquareQuote,
        title: 'Évaluation',
        desc: 'À la fin du parcours, le vendeur évalue votre accompagnement de façon vérifiée.',
    },
    {
        icon: Star,
        title: 'Réputation',
        desc: "L'avis s'ajoute à votre profil et à celui de votre agence — sous votre contrôle.",
    },
    {
        icon: Eye,
        title: 'Visibilité',
        desc: "Votre profil gagne en lisibilité dans les recherches locales et auprès des prescripteurs.",
    },
    {
        icon: Inbox,
        title: 'Demande entrante',
        desc: "Un vendeur identifie votre profil, vous contacte directement via Homly.",
    },
    {
        icon: RefreshCcw,
        title: 'Nouvel accompagnement',
        desc: 'La boucle recommence — chaque dossier enrichit votre réputation durable.',
    },
];

/**
 * ConvNetworkLoop — Iter162
 * Boucle effet réseau (6 étapes). Visuel : grille hexagonale légère qui
 * tourne autour de la phrase clé.
 */
export const ConvNetworkLoop = () => {
    return (
        <section className="relative py-20 md:py-32 border-t border-white/5 overflow-hidden" data-testid="network-loop-section">
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/4 top-1/3 w-[500px] h-[500px] rounded-full bg-[#681793]/12 blur-[140px]" />
                <div className="absolute right-1/4 bottom-1/4 w-[400px] h-[400px] rounded-full bg-[#FF914D]/10 blur-[140px]" />
            </div>

            <div className="relative max-w-6xl mx-auto px-6 md:px-12">
                <div className="max-w-3xl">
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                        Effet réseau
                    </span>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: '-80px' }}
                        transition={{ duration: 0.6 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                    >
                        Chaque accompagnement{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            nourrit la suivante.
                        </span>
                    </motion.h2>
                    <p className="mt-5 text-base md:text-lg text-[#A19DAB] leading-relaxed">
                        Le travail bien fait devient visible. La visibilité génère des
                        demandes. Les demandes mènent à de nouveaux dossiers. Et chaque
                        nouveau dossier renforce votre profil.
                    </p>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {STEPS.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <motion.div
                                key={s.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: '-60px' }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-6"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#A855F7]/20 to-[#FF914D]/20 flex items-center justify-center">
                                        <Icon className="w-4 h-4 text-white" />
                                    </span>
                                    <span className="text-[10px] font-mono tracking-widest text-[#6E6A7A]"
                                          style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                        {String(i + 1).padStart(2, '0')}
                                    </span>
                                </div>
                                <h3 className="font-semibold text-white" style={DISPLAY}>{s.title}</h3>
                                <p className="text-sm text-[#A19DAB] mt-1 leading-relaxed">{s.desc}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
